"use client";

import { useState } from "react";
import { useSafeAuth } from "@/hooks/use-safe-auth";

/**
 * Detects "guest user with no completed profile" for the
 * `ProfileSelectionDialog` — opened manually from the user menu only.
 *
 * Conditions (kept in sync with the original inline check in
 * `site-shell/header/components/Action/UserMenu.tsx`):
 *   - `role === "guest"` in `user.roles[].Role`
 *   - `profileState ∈ {created, incomplete, pending}` (JWT claim)
 *
 * Returned controls let header variants render the dialog and expose
 * a manual "Complete Profile" trigger (e.g. in a user dropdown menu)
 * (the only way it opens).
 */
export function useProfileCompletionPrompt(): {
  open: boolean;
  setOpen: (open: boolean) => void;
  needsCompletion: boolean;
  user: any;
} {
  const { user } = useSafeAuth();
  const [open, setOpen] = useState(false);

  // Roles may arrive as either `string[]` (legacy) or
  // `Array<{Role:string,Organization:string,Department:string}>`
  // (current JWT shape). Cover both so we don't silently no-op for
  // a tenant whose JWT serializer differs.
  const hasGuestRole = Boolean(
    user?.roles?.some(
      (r: any) =>
        (typeof r === "string" && r.toLowerCase() === "guest") ||
        r?.Role === "guest" ||
        r?.role === "guest",
    ),
  );
  const profileState = (user as any)?.profileState;
  const incompleteProfile =
    profileState === "created" ||
    profileState === "incomplete" ||
    profileState === "pending";

  const needsCompletion = hasGuestRole && incompleteProfile;

  // NO auto-open (2026-10-24): the popup used to open on every page for
  // every guest — it nagged library/reader guests and its Student path
  // skipped the /enroll admission gate. It now opens only from the user
  // menu's "Complete profile" item (setOpen).

  return { open, setOpen, needsCompletion, user };
}
